import { flow } from 'fp-ts/function';
import { InjectedFormProps } from 'redux-form';
import { connect } from 'react-redux';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { withAsyncContainer, FieldsContainer } from '@mews-commander/ui';
import { ChargeablePreauthorization } from '@mews-commander/data-access';
import { CommanderThunkDispatch, WithAccountId, WithBillId } from '@mews-commander/types';
import {
    commanderForm,
    formattedCurrency,
    InputField,
    presence,
    SelectField,
    validation as validate,
} from '@mews-commander/legacy-form';
import { push } from '@mews/routing';
import { CurrencyFormatHint } from '../../../../../../components/CurrencyFormatHint';
import { addChargePreauthorization } from '../../../../actions/api';
import { hidePageLoader, showPageLoader } from '../../../../../../core/pageLoader';
import { popHistory } from '../../../../../../core/history';
import { getFormOptions, getSelectedPreauthorizationAmount } from './utils';

interface ChargePreauthorizationPaymentFormValues {
    amount: string;
    preauthorizationId: string;
    receiptIdentifier?: string;
}

interface Props extends WithBillId, WithAccountId {
    chargeablePreauthorizations: ChargeablePreauthorization[];
    isGateway: boolean;
    invalidateQuery: () => void;
}

const ChargePreauthorizationForm = ({ chargeablePreauthorizations, isGateway, change }: Props & InjectedFormProps) => {
    const handlePreauthorizationChange = (preauthorizationId: string) => {
        const amount = getSelectedPreauthorizationAmount(chargeablePreauthorizations, preauthorizationId);
        change('amount', amount);
    };

    return (
        <FieldsContainer>
            <SelectField
                {...currencyProps}
                options={getFormOptions(chargeablePreauthorizations)}
                onChange={handlePreauthorizationChange}
            />
            <InputField {...amountProps} />
            {!isGateway && <InputField {...receiptIdentifierProps} />}
        </FieldsContainer>
    );
};

const currencyProps = {
    name: 'preauthorizationId',
    label: <LocalizedText textKey={TextKey.Preauthorization} />,
};

const amountProps = {
    name: 'amount',
    label: <LocalizedText textKey={TextKey.Amount} />,
    required: true,
    helper: <CurrencyFormatHint />,
};

const receiptIdentifierProps = {
    name: 'receiptIdentifier',
    label: <LocalizedText textKey={TextKey.ReceiptIdentifier} />,
};

const constraints = ({ initialValues: { amount } }: { initialValues: ChargePreauthorizationPaymentFormValues }) => ({
    amount: {
        presence,
        formattedCurrency,
        lowerThanCurrencyValue: {
            threshold: amount,
        },
    },
});

export default flow(
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Charge} />,
        validate: validate(constraints),
        submitButtonProps: { variant: 'primary' },
    }),
    connect(
        null,
        (
            dispatch: CommanderThunkDispatch,
            { accountId, billId, isGateway, invalidateQuery, chargeablePreauthorizations }: Props
        ) => ({
            onSubmit: async ({
                amount,
                preauthorizationId,
                receiptIdentifier,
            }: ChargePreauthorizationPaymentFormValues) => {
                const currency = chargeablePreauthorizations.find(({ id }) => id === preauthorizationId)?.amount
                    .currencyCode;

                try {
                    showPageLoader();
                    await dispatch(
                        addChargePreauthorization({
                            customerId: accountId,
                            billId,
                            isGateway,
                            amount,
                            preauthorizationId,
                            receiptIdentifier,
                            currency,
                        })
                    );
                } finally {
                    const url = popHistory();
                    hidePageLoader();
                    dispatch(push(url));
                    invalidateQuery();
                }
            },
        })
    ),
    withAsyncContainer
)(ChargePreauthorizationForm);
